/**
 * EntityAssistantDrawer — the assistant beside an entity create/edit form. Demo page: /chats (the thread) in a
 * non-modal side `Sheet`, so the form stays usable next to it.
 *
 * The conversation is the platform's one: `useAssistantConversation` opens it for the subject, `useConversationStream`
 * is its one data layer (backfill, live socket, poll, sending), and `ConversationChat` draws it on the assistant-ui
 * thread. The form as it is NOW goes out with every message (`contextSpec`); an apply block in the latest answer
 * becomes pills above the input that write back to the form.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Bot, Wifi, WifiOff } from 'lucide-react';
import { useAssistantConversation } from '../../hooks/useAssistantConversation.js';
import { useConversationStream, type StreamMessage } from '../../hooks/useConversationStream.js';
import { ConversationChat } from '../conversations/ConversationChat.js';
import { Badge } from '../ui/Badge.js';
import { Button } from '../ui/Button.js';
import { Callout } from '../ui/Callout.js';
import { RuntimePolicyChip } from '../ui/RuntimePolicyChip.js';
import { Sheet } from '../ui/Sheet.js';
import { AssistantApplyPills } from './AssistantApplyPills.js';
import { WorkflowAssistantApplyPanel } from './WorkflowAssistantApplyPanel.js';
import { assistantContextSpec } from './assistant-context-spec.js';
import type { ConversationEntryPoint } from '../../hooks/useAssistantConversation.js';
import type { ConversationEventsState } from '../../hooks/useConversationEvents.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AssistantSubjectType =
  | 'template'
  | 'tool'
  | 'daemon-draft'
  | 'workflow'
  | 'project'
  | 'project_thread'
  | 'project_change'
  | 'workstream'
  | 'strategy'
  | 'dashboard'
  | 'analysis_template';

const SUBJECT_LABELS: Record<AssistantSubjectType, string> = {
  template: 'Template Assistant',
  tool: 'Tool Assistant',
  'daemon-draft': 'Employee Assistant',
  workflow: 'Workflow Assistant',
  project: 'Project Assistant',
  project_thread: 'Project Thread Assistant',
  project_change: 'Project Change Assistant',
  workstream: 'Workstream Project Manager',
  strategy: 'Strategy Assistant',
  dashboard: 'Ask about this dashboard',
  analysis_template: 'Template Assistant',
};

/** What the empty conversation says; a subject not named here keeps the form-assistant line. */
const SUBJECT_EMPTY_TEXT: Partial<Record<AssistantSubjectType, string>> = {
  strategy:
    'Ask about this strategy and its runs: I can explain a result, propose settings for you to apply, or queue a backtest.',
  dashboard:
    'Ask about what this dashboard shows. I read its widgets and answer only from their numbers; work you ask for becomes a task.',
  analysis_template:
    'I can help you build this analysis template — prompt design, output schemas, input guidance — and propose fields for you to apply.',
};

export interface EntityAssistantDrawerProps<FormValues extends Record<string, unknown>> {
  subjectType: AssistantSubjectType;
  /** Persistent entity id — pass if the entity already exists (e.g. workflow edit) */
  subjectId?: string;
  /** Stable key for unsaved drafts (new-entity pages). Used when subjectId is null. */
  draftKey: string;
  /** Where in the app the assistant is opened from. The server names the persona for it. */
  entryPoint: ConversationEntryPoint;
  /** Live form values — sent as contextSpec when conversation is created. */
  currentFormValues: FormValues;
  /** Currently focused field key — reserved for future proactive help (Phase B). */
  fieldFocus?: string;
  /** Called when the user accepts one or more apply-pill suggestions. */
  onApplyFieldUpdate?: (partial: Partial<FormValues>) => void;
  /** Optional — workflow builder current step (embedded in contextSpec for now). */
  currentStepKey?: string;
  /** What the page knows that is not a form field (an employee's scope, the listing a draft came from). */
  contextExtras?: Record<string, unknown>;
  /** Optional seed message from host UI actions such as AI Collaborate imports. */
  queuedMessage?: string | null;
  /** Called after a queued message is successfully submitted. */
  onQueuedMessageConsumed?: () => void;
  /** Optional custom trigger; defaults to the global floating assistant button. */
  trigger?: (open: () => void) => ReactNode;
  /** The conversation's kind (`useAssistantConversation`); `user-agent` lets the persona act through entity skills. */
  conversationKind?: 'assistant-ui' | 'user-agent';
  /** Told when the assistant opens or closes (a page that polls for what the assistant starts). */
  onOpenChange?: (open: boolean) => void;
}

// ---------------------------------------------------------------------------
// Drawer content (rendered when open)
// ---------------------------------------------------------------------------

/** The text of the newest assistant answer: where the apply pills read the suggested field updates from. */
export function latestAssistantText(messages: readonly StreamMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message?.role === 'assistant' && message.content.trim()) return message.content;
  }
  return null;
}

function connectionStatus(state: ConversationEventsState): { label: string; tone: 'success' | 'warning' | 'danger' } {
  if (state.status === 'open')
    return { label: state.transport === 'rest' ? 'REST fallback' : 'Connected', tone: 'success' };
  if (state.status === 'connecting' || state.status === 'reconnecting') return { label: 'Connecting', tone: 'warning' };
  if (state.status === 'error') return { label: 'Disconnected', tone: 'danger' };
  return { label: 'Idle', tone: 'warning' };
}

function DrawerContent<FormValues extends Record<string, unknown>>({
  subjectType,
  subjectId,
  draftKey,
  entryPoint,
  currentFormValues,
  currentStepKey,
  contextExtras,
  onApplyFieldUpdate,
  queuedMessage,
  onQueuedMessageConsumed,
  conversationKind,
}: EntityAssistantDrawerProps<FormValues>) {
  const queuedMessageInFlightRef = useRef<string | null>(null);
  // The form, in the shape the engine's state hook for this subject reads (`assistant-context-spec.ts`).
  const liveContextSpec = useMemo(
    () =>
      assistantContextSpec(subjectType, currentFormValues as Record<string, unknown>, {
        currentStepKey,
        extras: contextExtras,
      }),
    [subjectType, currentFormValues, currentStepKey, contextExtras],
  );

  const {
    conversation,
    conversationId,
    isLoading: isOpening,
    error: openError,
  } = useAssistantConversation({
    subjectType,
    subjectId,
    draftKey,
    entryPoint,
    ...(conversationKind ? { kind: conversationKind } : {}),
    currentFormValues: currentFormValues as Record<string, unknown>,
    contextSpec: liveContextSpec,
  });

  // The one conversation data layer. The assistant sits beside a page of the full workspace, which is what it tells
  // the agent.
  const stream = useConversationStream(conversationId === null ? null : String(conversationId), 'detailed');
  const { eventsState, send } = stream;

  const isInitialLoading = (isOpening || stream.isLoading) && stream.messages.length === 0;
  const error = openError ?? (stream.isError ? (stream.error as Error) : null);
  const liveStatus = connectionStatus(eventsState);

  useEffect(() => {
    if (!queuedMessage?.trim()) queuedMessageInFlightRef.current = null;
  }, [queuedMessage]);

  useEffect(() => {
    const content = queuedMessage?.trim();
    if (!content || isInitialLoading || conversationId === null) return;
    if (queuedMessageInFlightRef.current === content) return;

    queuedMessageInFlightRef.current = content;
    send(content, {
      contextSpec: liveContextSpec,
      onSuccess: () => onQueuedMessageConsumed?.(),
      onError: () => {
        queuedMessageInFlightRef.current = null;
      },
    });
  }, [conversationId, isInitialLoading, liveContextSpec, onQueuedMessageConsumed, queuedMessage, send]);

  const latestAnswer = latestAssistantText(stream.messages);
  const applyPills =
    onApplyFieldUpdate && latestAnswer ? (
      subjectType === 'workflow' ? (
        <WorkflowAssistantApplyPanel<FormValues> latestMessage={latestAnswer} onApplyFieldUpdate={onApplyFieldUpdate} />
      ) : (
        <AssistantApplyPills<FormValues> latestMessage={latestAnswer} onApplyFieldUpdate={onApplyFieldUpdate} />
      )
    ) : null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-wrap items-center gap-1.5 pb-3">
        <RuntimePolicyChip policyId={conversation?.runtimePolicyId} />
        <Badge
          variant={liveStatus.tone}
          icon={liveStatus.tone === 'danger' ? <WifiOff size={11} /> : <Wifi size={11} />}
        >
          {liveStatus.label}
        </Badge>
        {eventsState.agentThinking && !eventsState.streamingMessage && (
          <span className="text-xs text-text-tertiary italic">thinking…</span>
        )}
      </div>
      {error && (
        <Callout tone="danger" title="The assistant could not open">
          {error.message}
        </Callout>
      )}
      <div className="-mx-5 -mb-4 min-h-0 flex-1 border-t border-card-border">
        <ConversationChat
          conversationId={conversationId === null ? null : String(conversationId)}
          surface="detailed"
          stream={stream}
          assistant
          contextSpec={liveContextSpec}
          composerExtra={applyPills}
          placeholder="Ask the assistant…"
          empty={
            <p className="text-sm text-text-tertiary">
              {SUBJECT_EMPTY_TEXT[subjectType] ?? 'Ask me anything about this form.'}
            </p>
          }
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component — open button + side sheet
// ---------------------------------------------------------------------------

export function EntityAssistantDrawer<FormValues extends Record<string, unknown>>(
  props: EntityAssistantDrawerProps<FormValues>,
) {
  const [open, setOpenState] = useState(false);
  const onOpenChange = props.onOpenChange;
  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );
  const handleClose = useCallback(() => setOpen(false), [setOpen]);

  useEffect(() => {
    if (props.queuedMessage?.trim()) setOpen(true);
  }, [props.queuedMessage, setOpen]);

  return (
    <>
      {/* Floating trigger button — bottom-right */}
      {!open &&
        (props.trigger?.(() => setOpen(true)) ?? (
          <Button
            iconOnly
            size="lg"
            aria-label="Open assistant"
            onClick={() => setOpen(true)}
            className="fixed right-6 bottom-6 z-40 size-12 rounded-full shadow-lg"
          >
            <Bot size={22} aria-hidden />
          </Button>
        ))}

      {/* Non-modal: the form beside the assistant stays usable (Escape and the X close it). */}
      <Sheet
        open={open}
        onClose={handleClose}
        modal={false}
        resizable={{ storageKey: 'confluent-assistant-width' }}
        title={SUBJECT_LABELS[props.subjectType]}
        titleIcon={<Bot size={16} aria-hidden />}
      >
        {open && <DrawerContent<FormValues> {...props} />}
      </Sheet>
    </>
  );
}
